// "Schools that offer it": the one reader of /api/public/offerings/by-courses/, used by the chat
// (RiasecResult), the Results page and the student PDF so they list the same schools.
import api from '../../../utils/api';
import { normalizeName } from './riasecLogic';

// The server reads about this many names a call (views.PUBLIC_OFFERING_NAMES_CAP)
export const NAMES_PER_CALL = 60;

/**
 * POST /api/public/offerings/by-courses/ for any number of names, in parts of NAMES_PER_CALL.
 * Resolves like one axios call: { data: { results } }. A part that fails rejects the whole call.
 */
export const postByCourses = async ({ names = [], ...rest } = {}, options = {}) => {
  const unique = [...new Set((Array.isArray(names) ? names : []).filter(Boolean))];
  const parts = [];
  for (let i = 0; i < unique.length; i += NAMES_PER_CALL) parts.push(unique.slice(i, i + NAMES_PER_CALL));
  if (!parts.length) return { data: { results: {} } };
  const replies = await Promise.all(parts.map((part) => api.post('/api/public/offerings/by-courses/', { names: part, ...rest }, options)));
  return { data: { results: Object.assign({}, ...replies.map((reply) => reply?.data?.results || {})) } };
};

const isDavao = (school) => /davao/i.test(`${school?.school_address || ''} ${school?.school_name || school?.name || ''}`);

/**
 * The schools that offer one program, from a by-courses reply (`data` is response.data): each
 * school once (its latest school year), the student's own school left out, schools in Davao first.
 */
export const otherSchoolsFor = (data, name, ownSchool = '') => {
  const entry = data?.results?.[normalizeName(name)];
  const own = normalizeName(ownSchool);
  const bySchool = new Map();
  (Array.isArray(entry?.schools) ? entry.schools : []).forEach((school) => {
    const label = school?.school_name || school?.name;
    if (!label || (own && normalizeName(label) === own)) return;
    const key = school.id != null ? `id:${school.id}` : `name:${normalizeName(label)}`;
    const kept = bySchool.get(key);
    if (!kept || String(school.school_year || '') > String(kept.school_year || '')) bySchool.set(key, { ...school, school_name: label });
  });
  return [...bySchool.values()].sort(
    (a, b) => Number(isDavao(b)) - Number(isDavao(a)) || String(a.school_name).localeCompare(String(b.school_name)),
  );
};

/** Lower-case names of the programs some other school offers, for the "Other schools" tag. */
export const elsewhereNamesFrom = (data, names, ownSchool = '') =>
  new Set(names.map(normalizeName).filter((key) => otherSchoolsFor(data, key, ownSchool).length > 0));
